// What a teacher is told about the price, in the places that are not the price
// list itself.
//
// There is one price (domain/billingPlans.js MONTHLY_PRICE_MINOR, #722). What
// is here:
//
//   * `checkoutDisclosure` — the line Stripe shows beside the Pay button
//     (functions/billing/callables.js `startCheckout`, `custom_text.submit`).
//   * `priceTerms` — what the trial banner quotes for after the trial.
//
// **Pure, and prose.** domain/entitlement.js keeps sentences out of the
// domain; these are the exception because surfaces on two sides of the network
// say the same thing about money. Every amount comes through domain/money.js
// `format`.
//
// No I/O, no clock, no Firebase: it is mirrored into functions/domain/ and
// loaded there by Node's resolver, like every file in this directory.

import { BILLING_CURRENCY } from './billingPlans.js';
import { format } from './money.js';

// The language every sentence here is written in, and the locale of a figure
// whose reader's Region is not known. The amounts are USD whatever the
// studio's own locale (BILLING_CURRENCY). Checkout is Stripe's page and the
// server passes it no Region, so its line is written in en-US. The trial
// banner passes the studio's locale for the number.
export const DISCLOSURE_LOCALE = 'en-US';

// Where a teacher cancels, named the way the app names it: the page is
// "Business Settings" (AppShell) and the tab is "Membership" (SettingsPage).
export const CANCEL_WHERE = 'Business Settings → Membership';

// Stripe's documented ceiling for `custom_text.submit.message`: "Text can be up
// to 1200 characters in length." (API reference, Create a Checkout Session.)
// Checked by a test against the wording below rather than truncated at run
// time — a disclosure cut off mid-sentence is worse than none.
export const CHECKOUT_TEXT_MAX = 1200;

const money = (minor, locale = DISCLOSURE_LOCALE) => format(minor, BILLING_CURRENCY, locale);

// The text Stripe shows beside the Pay button.
//
// Where to cancel, and **no price**. Stripe's own page shows the line item's
// price, which is the price Stripe is about to charge; restating it here would
// be a second source that can disagree — a discount applied on Stripe's side
// shows on the page but not in this string. Where to cancel is the one thing
// Checkout does not say.
//
// The same for every studio: it takes no account (#722).
export function checkoutDisclosure() {
  return `You can cancel anytime in ${CANCEL_WHERE}.`;
}

// The price a trial studio is quoted for afterwards: "$6.99 a month".
//
// `priceMinor` is the verdict's (domain/entitlement.js), which is the monthly
// price for every studio that is not comped (#722).
export function priceTerms({ priceMinor, locale = DISCLOSURE_LOCALE }) {
  return `${money(priceMinor, locale)} a month`;
}
